import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { POLICIES, POLICY_NAV } from '../data/policies';
import './PolicyPage.css';

function markdownToHtml(md) {
  if (!md) return '';
  let html = md
    // H1
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // H2
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    // H3
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Table header + separator rows → skip separator
    .replace(/^\|(.+)\|$/gm, (line) => {
      if (/^[\s|:-]+$/.test(line)) return '';
      const cells = line.split('|').filter((_, i, a) => i > 0 && i < a.length - 1);
      const isHeader = false;
      return `<tr>${cells.map(c => `<td>${c.trim()}</td>`).join('')}</tr>`;
    })
    // Ordered list items
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Unordered list items
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    // Blank lines → paragraph breaks
    .replace(/\n\n/g, '</p><p>')
    // Wrap consecutive <li> in <ul>
    .replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`);

  // Wrap table rows
  html = html.replace(/(<tr>.*<\/tr>\n?)+/gs, (match) => `<table>${match}</table>`);

  return `<p>${html}</p>`;
}

const ACCENT_DEFAULTS = {
  'help-center': '#0b7fab',
  career: '#d46c00',
  forum: '#7a3eb1',
  terms: '#195167',
  privacy: '#008c72',
  'community-guidelines': '#c0392b',
  cookies: '#9c6b1c',
  copyright: '#2c3e50',
};

export default function PolicyPage() {
  const { slug = 'privacy' } = useParams();
  const navigate = useNavigate();
  const page = POLICIES[slug];
  const accent = page?.accent || ACCENT_DEFAULTS[slug] || '#0EA5E9';

  useEffect(() => {
    window.scrollTo(0, 0);
    if (page) {
      document.title = `RivalCam | ${page.title}`;
    }
  }, [slug, page]);

  if (!page) {
    return (
      <div className="pp-not-found">
        <h2>Page not found</h2>
        <button onClick={() => navigate('/')}>Back to home</button>
      </div>
    );
  }

  const htmlContent = markdownToHtml(page.content);

  return (
    <div className="pp-root">
      {/* Hero */}
      <section
        className="pp-hero"
        style={{ background: `linear-gradient(135deg, ${accent}ee 0%, #030712 100%)` }}
      >
        <div className="pp-hero-inner">
          <p className="pp-eyebrow">RivalCam Resource Center</p>
          <h1>{page.title}</h1>
          {page.summary && <p className="pp-summary">{page.summary}</p>}
          <div className="pp-hero-actions">
            <button className="pp-btn" onClick={() => navigate('/')}>Back to Home</button>
            <a className="pp-btn pp-btn--ghost" href="mailto:support@meanc.net">Talk to Support</a>
          </div>
        </div>
      </section>

      {/* Logo badge */}
      <div className="pp-logo-badge">
        <img src="/rivalogo.png" alt="RivalCam" />
      </div>

      {/* Body */}
      <div className="pp-body">
        {/* Sidebar nav */}
        <nav className="pp-nav" aria-label="Policy pages">
          {POLICY_NAV.map((link) => (
            <Link
              key={link.slug}
              to={`/info/${link.slug}`}
              className={`pp-nav-link${link.slug === slug ? ' is-active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Content card */}
        <div className="pp-card">
          <div className="pp-card-header">
            <span className="pp-pill">Live</span>
            <span className="pp-updated">Last updated: {new Date().getFullYear()}</span>
          </div>
          <article
            className="pp-article"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>
      </div>

      {/* Footer */}
      <footer className="pp-footer">
        <div className="pp-footer-top">
          <div className="pp-footer-brand">
            <img src="/rivalogo.png" alt="RivalCam" className="pp-footer-logo" />
            <div>
              <strong>RIVALCAM</strong>
              <small>by MEANC Technologies</small>
            </div>
          </div>
          <div className="pp-footer-grid">
            <div className="pp-footer-col">
              <p className="pp-footer-heading">Product</p>
              <a href="/#features">Features</a>
              <a href="/#download">Download</a>
              <a href="https://meanc.net" target="_blank" rel="noreferrer">MEANC</a>
            </div>
            <div className="pp-footer-col">
              <p className="pp-footer-heading">Company</p>
              <Link to="/info/career">Career</Link>
              <Link to="/info/help-center">Help Center</Link>
              <Link to="/info/forum">Forum</Link>
            </div>
            <div className="pp-footer-col">
              <p className="pp-footer-heading">Legal</p>
              <Link to="/info/privacy">Privacy Policy</Link>
              <Link to="/info/terms">Terms & Conditions</Link>
              <Link to="/info/cookies">Cookies Policy</Link>
              <Link to="/info/copyright">Copyright Policy</Link>
            </div>
            <div className="pp-footer-col">
              <p className="pp-footer-heading">Contact</p>
              <a href="mailto:support@meanc.net">support@meanc.net</a>
              <a href="mailto:services@meanc.net">services@meanc.net</a>
            </div>
          </div>
        </div>
        <div className="pp-footer-bottom">
          <p>© {new Date().getFullYear()} MEANC Technologies – All Rights Reserved</p>
          <div className="pp-footer-bottom-links">
            {POLICY_NAV.map((link) => (
              <Link key={link.slug} to={`/info/${link.slug}`}>{link.label}</Link>
            ))}
          </div>
          <img src="/rivalogo.png" alt="RivalCam" className="pp-footer-bottom-logo" />
        </div>
      </footer>
    </div>
  );
}
